import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Loader2, Send, Palette, Download, Check } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_LABEL = {
  open: ["Offen", "bg-blue-100 text-blue-800"],
  in_progress: ["In Arbeit", "bg-amber-100 text-amber-800"],
  question: ["Rückfrage offen", "bg-violet-100 text-violet-800"],
  done: ["Fertig", "bg-emerald-100 text-emerald-800"],
};

export default function DesignReply() {
  const { token } = useParams();
  const [req, setReq] = useState(null);
  const [error, setError] = useState("");
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const load = () =>
    axios.get(`${BACKEND_URL}/api/public/design-replies/${token}`)
      .then(({ data }) => setReq(data))
      .catch((err) => setError(err.response?.data?.detail || "Anfrage nicht gefunden"));

  useEffect(() => { load(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    if (!text.trim()) return;
    setSending(true);
    try {
      await axios.post(`${BACKEND_URL}/api/public/design-replies/${token}`, { text });
      setText("");
      setSent(true);
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || "Senden fehlgeschlagen");
    } finally {
      setSending(false);
    }
  };

  if (error && !req)
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl border border-neutral-100 shadow-sm p-8 text-center text-sm text-neutral-500" data-testid="reply-error">{error}</div>
      </div>
    );
  if (!req)
    return <div className="min-h-screen bg-neutral-50 flex items-center justify-center text-neutral-400"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  const [stLabel, stCls] = STATUS_LABEL[req.status] || STATUS_LABEL.open;

  return (
    <div className="min-h-screen bg-neutral-50 py-8 px-4">
      <div className="max-w-2xl mx-auto space-y-5" data-testid="design-reply-page">
        <div className="flex items-center gap-3">
          <span className="h-11 w-11 rounded-xl bg-violet-600 text-white flex items-center justify-center"><Palette className="h-5 w-5" /></span>
          <div>
            <h1 className="text-xl font-bold text-neutral-900">{req.event_title}</h1>
            <span className={`inline-block text-[11px] font-bold px-2 py-0.5 rounded-full mt-1 ${stCls}`} data-testid="reply-status">{stLabel}</span>
          </div>
        </div>

        {(req.results || []).length > 0 && (
          <div className="bg-white rounded-2xl border border-emerald-200 shadow-sm p-5" data-testid="reply-results">
            <p className="text-sm font-bold text-emerald-700 mb-3">🎉 Fertige Designs zum Download</p>
            <div className="space-y-2">
              {req.results.map((r, i) => (
                <a key={r.id || i} href={`${BACKEND_URL}${r.url}`} target="_blank" rel="noreferrer" data-testid={`reply-result-${i}`}
                  className="flex items-center gap-2.5 rounded-xl border border-neutral-200 px-3.5 py-2.5 text-sm font-semibold text-neutral-700 hover:border-emerald-300 hover:bg-emerald-50 transition">
                  <Download className="h-4 w-4 text-emerald-600 shrink-0" />
                  <span className="truncate">{r.filename || `Design ${i + 1}`}</span>
                </a>
              ))}
            </div>
          </div>
        )}

        <div className="bg-white rounded-2xl border border-neutral-100 shadow-sm p-5">
          <p className="text-sm font-bold text-neutral-800 mb-3">Nachrichten zu deiner Anfrage</p>
          {(req.thread || []).length === 0 ? (
            <p className="text-sm text-neutral-400" data-testid="reply-thread-empty">Noch keine Nachrichten.</p>
          ) : (
            <div className="space-y-3" data-testid="reply-thread">
              {req.thread.map((m) => (
                <div key={m.id} className={`flex ${m.from === "requester" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm ${m.from === "requester" ? "bg-neutral-900 text-white" : "bg-violet-50 border border-violet-100 text-neutral-800"}`}>
                    <p className={`text-[10px] font-bold mb-0.5 ${m.from === "requester" ? "text-neutral-400" : "text-violet-600"}`}>
                      {m.from === "requester" ? "Du" : `Design-Team · ${m.author || ""}`}
                    </p>
                    <p className="whitespace-pre-wrap">{m.text}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="mt-4 pt-4 border-t border-neutral-100">
            {sent && <p className="text-xs font-semibold text-emerald-600 mb-2 flex items-center gap-1" data-testid="reply-sent"><Check className="h-3.5 w-3.5" /> Antwort gesendet — das Design-Team wurde benachrichtigt.</p>}
            <textarea data-testid="reply-input" value={text} onChange={(e) => setText(e.target.value)} rows={3}
              placeholder="Deine Antwort an das Design-Team …"
              className="w-full px-3 py-2.5 rounded-xl border border-neutral-200 text-sm focus:outline-none focus:ring-2 focus:ring-violet-400 bg-white resize-none" />
            {error && <p className="text-xs font-semibold text-rose-600 mt-1">{error}</p>}
            <button data-testid="reply-send" onClick={submit} disabled={sending || !text.trim()}
              className="mt-2 inline-flex items-center gap-2 text-sm font-bold px-4 py-2.5 rounded-xl bg-violet-600 text-white hover:bg-violet-700 transition disabled:opacity-50">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Antwort senden
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
